/**
 * D332: the install prompt. Shown once the world is open, not on the very first screen, when
 * the game is running in a browser tab rather than installed. Dismissing it hides it for a
 * week; turning off "Remind me to install" in settings (`InstallSetting` below) hides it for
 * good. Android Chrome and some desktop Chromium browsers get an Install button from the
 * captured `beforeinstallprompt`; everyone else gets the steps for their device.
 */
import { useState } from 'preact/hooks';
import { Button } from '../ui/Button.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Switch } from './Switch.tsx';
import {
  INSTALL_STEPS,
  dismissInstallPrompt,
  installKind,
  isInstalled,
  promptInstall,
  readInstallEnv,
  useHasInstallPrompt,
  useInstallRemind,
  useInstallSnoozedUntil,
} from './install.ts';
import { claimPromptSlot } from './account-prompt.ts';
import { useIdle } from './store.ts';

export function InstallPrompt() {
  const now = useIdle((s) => s.now);
  const [remind] = useInstallRemind();
  const snoozedUntil = useInstallSnoozedUntil();
  const canPrompt = useHasInstallPrompt();
  const [env] = useState(readInstallEnv);
  if (!remind || isInstalled(env) || now < snoozedUntil) return null;
  // D414: one card a visit; this one has the slot unless the guest reminder took it.
  if (!claimPromptSlot('install')) return null;
  const kind = installKind(env);
  const offerButton = canPrompt && (kind === 'android' || kind === 'desktop');
  // D442: a band between the header and the map, full width, `surface-2` ruled above and below.
  return (
    <div
      data-testid="install-prompt"
      role="status"
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '4px 0 4px 12px',
        background: 'var(--surface-2)',
        borderTop: '1px solid var(--line)',
        borderBottom: '1px solid var(--line)',
        font: '400 12px/1.35 var(--font-ui)',
        color: 'var(--ink)',
        flex: 'none',
      }}
    >
      <span style={{ display: 'inline-flex', flex: 'none', color: 'var(--accent)' }}>
        <Icon name="box" size={20} />
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
        }}
      >
        Install Idle Worlds for a faster launch and a home screen icon.{' '}
        {!offerButton && INSTALL_STEPS[kind]}
      </span>
      {offerButton && (
        <Button variant="primary" onClick={() => void promptInstall()}>
          Install
        </Button>
      )}
      <button
        type="button"
        aria-label="Close"
        data-testid="install-prompt-dismiss"
        onClick={() => dismissInstallPrompt(now)}
        style={{
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
          width: 44,
          height: 44,
          border: 'none',
          background: 'transparent',
          color: 'var(--ink)',
        }}
      >
        <Icon name="x" size={18} />
      </button>
    </div>
  );
}

const SETTINGS_LABEL = { font: '600 var(--fs-name)/1.2 var(--font-ui)' };

/** The settings row: "Remind me to install", on by default (D332). */
export function InstallSetting() {
  const [remind, setRemind] = useInstallRemind();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={SETTINGS_LABEL}>Remind me to install</span>
      <Switch label="Remind me to install" on={remind} onChange={setRemind} />
    </div>
  );
}
